// Portal-based Modal. Closes on Esc + backdrop click. Locks body scroll while open.
//
// Usage:
//   <Modal title="…" onClose={…} size="md">{children}</Modal>

import { createPortal } from "preact/compat";
import { useEffect, useRef } from "preact/hooks";
import type { ComponentChildren } from "preact";
import { X } from "lucide-preact";
import { confirmDialog } from "../lib/confirm";

interface ModalProps {
  title: string;
  onClose: () => void;
  children: ComponentChildren;
  size?: "sm" | "md" | "lg" | "xl" | "2xl";
  /** Hide the default header (caller draws its own). */
  hideHeader?: boolean;
  /** Optional footer slot — renders inside the same modal panel below content. */
  footer?: ComponentChildren;
  /** "center" (default, classic dialog), "right" (Monday-style slide-over)
   *  or "bottom" (mobiles Bottom-Sheet, UX-Vorschlag P7). */
  placement?: "center" | "right" | "bottom";
  /** When true, Esc / backdrop / X ask before discarding (unsaved changes). */
  dirty?: boolean;
}

const SIZE_CLASSES: Record<NonNullable<ModalProps["size"]>, string> = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
  "2xl": "max-w-6xl",
};

const SLIDE_SIZE_CLASSES: Record<NonNullable<ModalProps["size"]>, string> = {
  sm: "w-full sm:max-w-md",
  md: "w-full sm:max-w-lg",
  lg: "w-full sm:max-w-2xl",
  xl: "w-full sm:max-w-3xl",
  "2xl": "w-full sm:max-w-5xl",
};

// Open-modal stack: with nested modals (e.g. confirm on top of an edit dialog)
// only the TOPMOST one may react to Escape — otherwise one keypress closes
// every layer at once.
const modalStack: symbol[] = [];

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal(props: ModalProps): preact.JSX.Element {
  const { title, onClose, children, size = "md", hideHeader, footer, placement = "center", dirty } = props;
  const panelRef = useRef<HTMLDivElement>(null);
  const stackIdRef = useRef(Symbol("modal"));
  const isSlide = placement === "right";

  // Close request that honors unsaved changes: dirty forms get a themed
  // confirm before the modal (and the user's input) is discarded.
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const requestClose = (): void => {
    if (!dirtyRef.current) {
      onCloseRef.current();
      return;
    }
    void confirmDialog({
      title: "Änderungen verwerfen?",
      message: "Ungespeicherte Änderungen gehen verloren.",
      confirmLabel: "Verwerfen",
      danger: true,
    }).then((ok) => {
      if (ok) onCloseRef.current();
    });
  };
  const requestCloseRef = useRef(requestClose);
  requestCloseRef.current = requestClose;

  // Register in the modal stack for the lifetime of this instance.
  useEffect(() => {
    const id = stackIdRef.current;
    modalStack.push(id);
    return () => {
      const i = modalStack.indexOf(id);
      if (i >= 0) modalStack.splice(i, 1);
    };
  }, []);

  // Esc closes — topmost modal only.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== "Escape") return;
      if (modalStack[modalStack.length - 1] !== stackIdRef.current) return;
      e.stopPropagation();
      requestCloseRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Focus trap: Tab / Shift-Tab cycle inside the dialog instead of escaping
  // into the (inert-looking but still tabbable) page behind the overlay.
  const trapTab = (e: KeyboardEvent): void => {
    if (e.key !== "Tab") return;
    const panel = panelRef.current;
    if (!panel) return;
    const focusables = Array.from(
      panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
    ).filter((el) => el.offsetParent !== null || el === document.activeElement);
    if (focusables.length === 0) {
      e.preventDefault();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (e.shiftKey) {
      if (active === first || active === panel) {
        e.preventDefault();
        last.focus();
      }
    } else if (active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  // Body scroll lock while modal is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Focus the panel on mount for keyboard users, and restore focus to whatever
  // was focused before the modal opened (usually its trigger) once it closes —
  // so keyboard/AT users aren't dumped at the top of the document.
  useEffect(() => {
    const prevFocused = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    return () => {
      if (prevFocused && typeof prevFocused.focus === "function" && document.contains(prevFocused)) {
        prevFocused.focus();
      }
    };
  }, []);

  const node = placement === "bottom" ? (
    <div
      class="juuplan-overlay-in fixed inset-0 z-50 flex items-end justify-center bg-[color:var(--color-on-surface)]/40"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) requestClose();
      }}
      role="presentation"
    >
      <div
        ref={panelRef}
        class="w-full max-h-[80dvh] flex flex-col rounded-t-xl bg-[color:var(--color-surface-container-lowest)] shadow-level-3 focus:outline-none pb-[env(safe-area-inset-bottom)]"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onKeyDown={trapTab}
      >
        {!hideHeader && (
          <header class="flex items-center justify-between gap-4 px-4 py-3 border-b border-[color:var(--color-outline-variant)]">
            <h2 class="text-base font-semibold text-[color:var(--color-on-surface)] truncate">{title}</h2>
            <button
              type="button"
              onClick={requestClose}
              class="rounded p-2 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
              aria-label="Schließen"
            >
              <X size={20} aria-hidden="true" />
            </button>
          </header>
        )}
        <div class="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 py-3">{children}</div>
      </div>
    </div>
  ) : isSlide ? (
    <div
      class="juuplan-overlay-in fixed inset-0 z-50 flex justify-end bg-[color:var(--color-on-surface)]/40 backdrop-blur-[2px]"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) requestClose();
      }}
      role="presentation"
    >
      <div
        ref={panelRef}
        class={`juuplan-slide-over ${SLIDE_SIZE_CLASSES[size]} h-full max-h-[100dvh] flex flex-col bg-[color:var(--color-surface-container-lowest)] shadow-level-3 sm:border-l border-[color:var(--color-outline-variant)] focus:outline-none`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onKeyDown={trapTab}
      >
        {!hideHeader && (
          <header class="flex items-center justify-between gap-4 px-5 py-4 border-b border-[color:var(--color-outline-variant)]">
            <h2 class="text-base font-semibold text-[color:var(--color-on-surface)] truncate">
              {title}
            </h2>
            <button
              type="button"
              onClick={requestClose}
              class="rounded p-1.5 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors"
              aria-label="Schließen"
            >
              <X size={20} aria-hidden="true" />
            </button>
          </header>
        )}
        <div class="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-5 py-4">{children}</div>
        {footer && (
          <footer class="px-5 py-3 border-t border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)]">
            {footer}
          </footer>
        )}
      </div>
    </div>
  ) : (
    <div
      class="juuplan-overlay-in fixed inset-0 z-50 flex items-stretch sm:items-center justify-center sm:p-4 bg-[color:var(--color-on-surface)]/40 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) requestClose();
      }}
      role="presentation"
    >
      <div
        ref={panelRef}
        class={`juuplan-modal-in w-full ${SIZE_CLASSES[size]} h-[100dvh] sm:h-auto sm:max-h-[90vh] flex flex-col sm:rounded-lg bg-[color:var(--color-surface-container-lowest)] shadow-level-3 sm:border border-[color:var(--color-outline-variant)] focus:outline-none`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onKeyDown={trapTab}
      >
        {!hideHeader && (
          <header class="flex items-center justify-between gap-4 px-5 py-4 border-b border-[color:var(--color-outline-variant)]">
            <h2 class="text-base font-semibold text-[color:var(--color-on-surface)] truncate">
              {title}
            </h2>
            <button
              type="button"
              onClick={requestClose}
              class="rounded p-1.5 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--color-surface-container-lowest)] transition-colors"
              aria-label="Schließen"
            >
              <X size={20} aria-hidden="true" />
            </button>
          </header>
        )}
        <div class="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-5 py-4">{children}</div>
        {footer && (
          <footer class="px-5 py-3 border-t border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] rounded-b-lg">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );

  return createPortal(node, document.body);
}
